import { merge, find, map, sortBy, isEmpty } from 'lodash';
import { APP_CONFIG } from '@/config/common';
import { IAemPricePlanGroup, IPricePlanGroup, IPriceplans, IPlanFeatures, IExistingVas } from './models/catalogueModel';

export const getMappedPricePlanGroup = (
  pricePlanGroup: IPricePlanGroup[] = [],
  aemPricePlanGroup: IAemPricePlanGroup[] = []
) => {
  return pricePlanGroup.map((group) =>
    merge(
      group,
      find(aemPricePlanGroup, ({ groupId }) => group.groupId === groupId)
    )
  );
};

export const getPricePlanFeatures = (plan) => {
  const planFeatures = APP_CONFIG['planFeatures'];
  return planFeatures
    .map((feature) => ({
      ...feature,
      value: plan[feature.key]
    }))
    .filter(({ value }) => value && value !== 'NA');
};

export const getPricPlanByContractTerm = ({ pricePlans = [], groupId }, contractTerm) => {
  return sortBy(
    pricePlans
      .filter((plan: IPriceplans) =>
        contractTerm === 'Others'
          ? !['12', '24', '36'].includes(plan?.contractTerms)
          : plan?.contractTerms === contractTerm
      ) //Others plan will contains plans other than base plan 12,24,36
      .map((plan: IPriceplans) => ({
        ...plan,
        value: plan.planId,
        name: plan.planName,
        planFeatures: getPricePlanFeatures(plan),
        groupId
      })),
    'name'
  );
};

export const getContractTermList = () =>
  map(['12', '24', '36', 'Others'], (contractTerms) => ({
    value: contractTerms,
    label: contractTerms === 'Others' ? contractTerms : contractTerms + ' Month'
  }));

const getcontractTerms = (contractTerms) => {
  const contractTerm = contractTerms === '0' ? 'No Contract' : contractTerms + ' Month';
  return contractTerm;
}

export const getChangePlanContractTermList = () =>
  map(['0', '12', '24', '36', 'Others'], (contractTerms) => ({
    value: contractTerms,
    label: ['Others'].includes(contractTerms) ? contractTerms : getcontractTerms(contractTerms)
  }));

export const getContractTerm = (orderType) => {
  if (!orderType) return '';
  const defaultTerm = APP_CONFIG[orderType]['defaultTerm'];
  const contractTermList = getContractTermList();
  return (contractTermList.find(({ value }) => value === defaultTerm) || contractTermList[0])?.value;
};

export const getCurrentSelectionForPricePlan = (currentOrder, groups: IPricePlanGroup[] = []) => {
  const { shoppingCartType: orderType, cartDetails: { pricePlan = {} } = {} } = currentOrder;
  const termValue = getContractTerm(orderType?.toUpperCase());
  const groupIndexSelected = groups.reduce((gIndex, group, index) => {
    if (group?.groupId === pricePlan?.pricePlanGroupId && pricePlan?.planTypeCode !== APP_CONFIG.MOBILESHARE_PLAN_TYPE) return index;
    if (pricePlan?.planTypeCode === APP_CONFIG.MOBILESHARE_PLAN_TYPE) {
      if (group?.groupId === APP_CONFIG.MOBILESHARE_PLAN) return index;
    }
    return gIndex;
  }, 0);
  const pricePlans = groups[groupIndexSelected]?.pricePlans || [];
  const selectedPlan = pricePlans?.find((plan) => plan?.planId === pricePlan?.pricePlanId);
  return {
    contractTermSelected: selectedPlan?.contractTerms || termValue,
    groupIndexSelected
  };
};

export const getPricePlanGroup = (groupId: string, planGroups: IPricePlanGroup[] = []) =>
  planGroups.find((it) => it.groupId === groupId);

export const getPricePlan = (groupId: string, planId: number | string, planGroups: IPricePlanGroup[] = []) => {
  const planGroup = getPricePlanGroup(groupId, planGroups);
  const plan = (planGroup?.pricePlans || []).find((it) => it.planId === planId);
  return plan;
};

export const getBundledVasData = (groupId, planId, planGroups = []) => {
  const pricePlan = getPricePlan(groupId, planId, planGroups);
  return pricePlan?.bundledAddons || [];
};

export const hasBomEquipment = (order) => order.cartDetails?.equipments?.some((eq) => !isEmpty(eq.bomEquipments));

export const getPayload = (auth, order, otherPayload = {}) => {
  const { msaheaderId, brn } = auth.currentAccount || {};
  const { cartDetails, shoppingCartType, shoppingCartId } = order;
  const planId = cartDetails?.pricePlan?.pricePlanId;
  const earlyRecon = cartDetails.isEarlyRecon
  return {
    shoppingCartId,
    msaHeaderId: msaheaderId,
    transactionType: earlyRecon ? APP_CONFIG.EARLY_RECONTRACT : shoppingCartType,
    brn,
    planId: planId || '',
    ...otherPayload
  };
};

export const getAddonPlanList = ({ planFeatures = [] as IPlanFeatures[] }) =>
  planFeatures.filter((feature: IPlanFeatures) => feature.value && feature.value !== 'N/A');

export const getMappedServiceNumbers = (serviceNumbers, existingVas: IExistingVas[] = []) => {
  return serviceNumbers?.map((number) => {
    const serviceNumberVas = existingVas.find((item) => item.serviceNumber === number.number);
    let modifiedVas = [];
    if (serviceNumberVas) {
      modifiedVas = serviceNumberVas.existingVas.map((item) => {
        return {
          ...item,
          ['isRemoved']: false
        };
      });
    }
    const additionalProperties = serviceNumberVas
      ? {
        bundledAddons: serviceNumberVas.bundledVases,
        existingAddons: modifiedVas,
        penalties: serviceNumberVas.penalties,
        existingPricePlan: serviceNumberVas.existingPricePlan
      }
      : {};

    return {
      ...number,
      ...additionalProperties
    };
  });
};

export const getServiceNumberArray = (serviceNumbers) => {
  const serviceNumberArray = serviceNumbers?.map((sn) => (sn.serviceNumber ? sn.serviceNumber : sn.number));
  return serviceNumberArray;
};

export const calculatePenalty = (serviceNumberVases) => {
  const penalty = serviceNumberVases?.map((number) => {
    const p = number.existingAddons?.reduce((sum, b) => {
      const pen = b.isRemoved ? b.penaltyAmount : 0;
      return sum + pen;
    }, 0);
    return p;
  });
  return penalty;
};

export const getDetailsWithSerValue = (pricePlanData, serData) => {
  return pricePlanData?.map((ele) => {
    const serEquipListName = [...new Set(serData.map((data) => data.modelCode))];
    ele['serDevice'] = serEquipListName.includes(ele.modelCode);
    return ele;
  });
};
